import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authStorage } from '~/services/authStorage';
import { getDeviceId } from '~/services/deviceId';
import { apiService, setUnauthorizedHandler, scheduleTokenRefresh, stopTokenRefresh } from '~/services/apiService';
import { useGlobalContext } from '~/context/GlobalContext';
import { IAuthUser } from '~/types/common';

export function useAuth() {
  const navigate = useNavigate();
  const { context, setContext } = useGlobalContext();

  // Całkowite wylogowanie z czatu: serwer + API auth + WebRTC + magazyny + stan.
  const logout = useCallback(async () => {
    // 1. Powiadom serwer czatu o wylogowaniu — synchronicznie, póki w sessionStorage
    //    nadal istnieje 'user' (Websocket.handleLogOut czyta go stamtąd).
    try {
      window.dispatchEvent(new CustomEvent('logOut', {
        bubbles: true,
        detail: { user: { id: context.user?.id } },
      }));
    } catch {
      // ignorujemy — to tylko powiadomienie serwera
    }

    // 2. Wyloguj z API auth (unieważnienie tokena JWT po stronie backendu).
    const token = authStorage.getAccessToken();
    if (token) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // best effort — wyloguj lokalnie nawet jeśli request się nie powiedzie
      }
    }

    // 3. Twarde sprzątanie WebRTC: gaśnie dioda kamery, zamykają się połączenia P2P.
    //    Zdarzenie łapie useWebRTC (np. w VideoContainer / Roulette) i woła cleanup().
    try {
      window.dispatchEvent(new CustomEvent('forceCleanupWebRTC'));
    } catch {
      // ignorujemy
    }
    // Dodatkowo zatrzymujemy strumienie ze stanu, gdyby żaden useWebRTC nie był zamontowany.
    try {
      context.localStream?.getTracks().forEach(track => track.stop());
      context.remoteStream?.getTracks().forEach(track => track.stop());
    } catch {
      // ignorujemy — strumienie mogły już zostać zatrzymane
    }

    // 4. Zatrzymaj automatyczne odświeżanie tokenów.
    stopTokenRefresh();

    // 5. Wyczyść TYLKO dane związane z sesją (tokeny auth + dane użytkownika sesji).
    //    Ustawienia kosmetyczne (motyw, dźwięk, wibracje, zgoda na cookies) zostają.
    authStorage.clear();
    try {
      window.sessionStorage.removeItem('user');
      window.sessionStorage.removeItem('logout_reason');
    } catch {
      // ignorujemy
    }

    // 6. Zresetuj cały stan globalny aplikacji.
    setContext({
      authUser: null,
      user: null,
      room: null,
      rooms: [],
      publicRooms: null,
      privateWaitingUsers: [],
      callSession: null,
      localStream: null,
      remoteStream: null,
      searchQuery: '',
      activeSidebarId: '',
    });

    // 7. Wróć na stronę główną.
    window.location.href = '/';
  }, [navigate, setContext, context.user?.id, context.localStream, context.remoteStream]);

  const login = useCallback(async (name: string, password: string, rememberMe: boolean = false): Promise<IAuthUser> => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, password, rememberMe, deviceId: getDeviceId() }),
    });

    const data = await res.json();

    if (!res.ok || !data.result) {
      throw new Error(data.message || 'Niepoprawny login lub hasło.');
    }

    const { access_token, refresh_token, user } = data.result;

    authStorage.setTokens(access_token, refresh_token, rememberMe);
    setUnauthorizedHandler(logout);
    scheduleTokenRefresh(logout);
    setContext({ authUser: user });

    return user;
  }, [logout, setContext]);

  const joinRoom = useCallback(async (user: IAuthUser): Promise<boolean> => {
    try {
      const rooms: any[] = await apiService('/chat/rooms', 'GET');
      const generalRoom = rooms.find((r: any) => r.label === 'general') ?? rooms[0];
      if (!generalRoom) return false;

      const token = authStorage.getAccessToken();
      const res = await fetch('/api/chat/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ name: user.nick, gender: user.gender, room: generalRoom.id }),
      });
      const data = await res.json();

      if (data?.result?.isUserNameExist === false) {
        navigate('/chat', {
          state: { room: data.result.room, name: data.result.name, gender: data.result.gender },
        });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [navigate]);

  // Wywołać przy starcie aplikacji — przywraca sesję po odświeżeniu strony
  const initAuth = useCallback(async (): Promise<IAuthUser | null> => {
    const token = authStorage.getAccessToken();
    if (!token) return null;

    // Ustaw handlery zanim zrobimy request — apiService może wywołać onUnauthorized
    setUnauthorizedHandler(logout);
    scheduleTokenRefresh(logout);

    try {
      // apiService automatycznie obsłuży 401 → refresh → ponowienie
      const user: IAuthUser = await apiService('/auth/profile', 'GET');
      setContext({ authUser: user });
      return user;
    } catch {
      authStorage.clear();
      return null;
    }
  }, [logout, setContext]);

  return { login, logout, initAuth, joinRoom };
}
